import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link, Navigate } from 'react-router-dom'
import { isAllowedEmail, supabase, ALLOWED_DOMAIN } from '../lib/supabase.js'
import { useParticipant } from '../hooks/useParticipant.js'

export default function SignIn() {
  const { participant, status } = useParticipant()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-brand-blue animate-pulse" />
          <span className="text-brand-text-muted text-sm">Loading…</span>
        </div>
      </div>
    )
  }
  if (participant) return <Navigate to="/" replace />

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    if (!isAllowedEmail(email)) {
      setError(`Use your @${ALLOWED_DOMAIN} email.`)
      return
    }
    if (!password) {
      setError('Password required.')
      return
    }
    setSubmitting(true)
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      })
      if (error) throw error
    } catch (err) {
      setError(err.message || 'Sign in failed. Check email + password.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen mesh-bg flex flex-col">
      <header className="px-6 py-4">
        <Link to="/welcome" className="inline-block">
          <img src="/climatise-logo.svg" alt="Climatise" className="h-8" />
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-card p-8 border border-brand-border"
        >
          <h2 className="text-2xl font-bold text-brand-text mb-1">Sign in</h2>
          <p className="text-sm text-brand-text-muted mb-6">
            Use your Climatise email and the temp password Lennon shared.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-brand-text">Work email</span>
              <input
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={`you@${ALLOWED_DOMAIN}`}
                className="mt-1 w-full px-4 py-3 border border-brand-border rounded-lg text-base focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue-tint"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-brand-text">Password</span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="mt-1 w-full px-4 py-3 border border-brand-border rounded-lg text-base focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue-tint"
              />
            </label>

            {error && (
              <div className="text-sm text-brand-danger bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-blue hover:bg-brand-blue-hover text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60"
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="text-xs text-brand-text-muted mt-6 text-center">
            Forgot it? Ping Lennon.
          </p>
        </motion.div>
      </div>
    </div>
  )
}
